import React, { useState, useEffect } from 'react';
import { AutoComplete, Select } from 'antd';
import { getLookups, LookupValue } from '../services/adminService';

interface LookupSelectProps {
  category: string;
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  allowCreate?: boolean;
  // When provided, restrict the dropdown to only the listed values.
  // Used to narrow the role list to roles the caller is allowed to assign.
  allowedValues?: string[];
  // 'autocomplete' (default) — typing new values is allowed, the input shows
  //   whatever string the user typed/picked (the value, e.g. "reseller_support").
  // 'select' — strict picker, no free-text, the input shows the human label
  //   ("Reseller Support") after a selection.
  mode?: 'autocomplete' | 'select';
}

const LookupSelect: React.FC<LookupSelectProps> = ({
  category,
  value,
  onChange,
  placeholder,
  disabled = false,
  allowedValues,
  mode = 'autocomplete',
}) => {
  const [options, setOptions] = useState<{ value: string; label: string }[]>([]);
  const [loaded, setLoaded] = useState(false);

  const fetchOptions = async () => {
    if (loaded) return;
    try {
      const lookups = await getLookups(category);
      const mapped = lookups.map((l: LookupValue) => ({ value: l.value, label: l.label }));
      setOptions(mapped);
      setLoaded(true);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    fetchOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  const visibleOptions = allowedValues
    ? options.filter(o => allowedValues.includes(o.value))
    : options;

  if (mode === 'select') {
    return (
      <Select
        value={value}
        onChange={onChange}
        options={visibleOptions}
        placeholder={placeholder}
        disabled={disabled}
        showSearch
        optionFilterProp="label"
        style={{ width: '100%' }}
      />
    );
  }

  return (
    <AutoComplete
      value={value}
      onChange={onChange}
      options={visibleOptions}
      placeholder={placeholder}
      disabled={disabled}
      filterOption={(input, option) =>
        (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase()) ||
        (option?.value ?? '').toString().toLowerCase().includes(input.toLowerCase())
      }
      style={{ width: '100%' }}
    />
  );
};

export default LookupSelect;
